import { BankAccount, UserSettings } from 'models';
import { isSameDay } from 'date-fns';
import _set from 'lodash/set';
import _isEqual from 'lodash/isEqual';

import { ComponentState } from './person-information';

export enum Label {
  Required = 'Lisää puuttuva tieto',
  Invalid = 'Syötä tieto oikeassa muodossa',
}

export function validateBankAccounts(bankAccounts: BankAccount[]) {
  const bankAccountNumberPattern = new RegExp(/^FI[0-9]{16}$/);
  let error = {};

  for (let i = 0, l = bankAccounts.length; i < l; i++) {
    const bankAccount = bankAccounts[i];

    if (!bankAccount.Number) {
      _set(error, `BankAccount_${i}_Number`, Label.Required);
      break;
    }

    if (!bankAccountNumberPattern.test(bankAccount.Number)) {
      _set(error, `BankAccount_${i}_Number`, Label.Invalid);
      break;
    }

    if (!bankAccount.IsReimbursement && !bankAccount.IsCommission) {
      _set(
        error,
        `BankAccount_${i}_IsReimbursement`,
        'Valitse pankkitilin käyttötarkoitus.'
      );
      break;
    }
  }

  return error;
}

export const collectData = (
  settings:
    | UserSettings
    | (Omit<UserSettings, 'HasSocialSecurityNumber'> & {
        SocialSecurityNumber: string | null;
      }),
  state: ComponentState
) => {
  return {
    hasSSN: state.hasSSN,
    birthday: settings.Birthday,
    email: settings.Email?.Email,
    phone: settings.Phone?.Phone,
    street1: settings.Address?.Street1,
    street2: settings.Address?.Street2,
    street3: settings.Address?.Street3,
    postalCode: settings.Address?.PostalCode,
    postalArea: settings.Address?.PostalArea,
    country: settings.Address?.Country,
    openAlternative: state.openAlternative,
    useAlternativeEmail: state.useAlternativeEmail,
    alternativeEmail: settings.AlternativeMailEmail?.Email,
    useAlternativeAddress: state.useAlternativeAddress,
    alternativeStreet1: settings.AlternativeMailAddress?.Street1,
    alternativeStreet2: settings.AlternativeMailAddress?.Street2,
    alternativeStreet3: settings.AlternativeMailAddress?.Street3,
    alternativePostalCode: settings.AlternativeMailAddress?.PostalCode,
    alternativePostalArea: settings.AlternativeMailAddress?.PostalArea,
    alternativeCountry: settings.AlternativeMailAddress?.Country,
    useMailForMeeting: state.useMailForMeeting,
    useMailForRelease: state.useMailForRealise,
    useAddressForMeeting: state.useAddressForMeeting,
    useAddressForRelease: state.useAddressForRealise,
    marketingPermission: settings.MarketingPermission,
    bankAccounts: settings.BankAccounts,
    tax: settings.Tax,
  };
};

export type CollectedData = ReturnType<typeof collectData>;

export const compareSettings = (prev: CollectedData, next: CollectedData) => {
  if (prev.hasSSN !== next.hasSSN) return false;
  if (prev.birthday && !next.birthday) return false;
  if (!prev.birthday && next.birthday) return false;
  if (prev.birthday && next.birthday) {
    if (!isSameDay(new Date(prev.birthday), new Date(next.birthday)))
      return false;
  }
  if (prev.email !== next.email) return false;
  if (prev.phone !== next.phone) return false;
  if (prev.street1 !== next.street1) return false;
  if (prev.street2 !== next.street2) return false;
  if (prev.street3 !== next.street3) return false;
  if (prev.postalArea !== next.postalArea) return false;
  if (prev.postalCode !== next.postalCode) return false;
  if (prev.country !== next.country) return false;
  if (prev.openAlternative !== next.openAlternative) return false;
  if (prev.openAlternative && next.openAlternative) {
    if (prev.useAlternativeEmail !== next.useAlternativeEmail) return false;
    if (prev.useAlternativeEmail && next.useAlternativeEmail) {
      if (prev.alternativeEmail !== next.alternativeEmail) return false;
    }
    if (prev.useAlternativeAddress !== next.useAlternativeAddress) return false;
    if (prev.useAlternativeAddress && next.useAlternativeAddress) {
      if (prev.alternativeStreet1 !== next.alternativeStreet1) return false;
      if (prev.alternativeStreet2 !== next.alternativeStreet2) return false;
      if (prev.alternativeStreet3 !== next.alternativeStreet3) return false;
      if (prev.alternativePostalArea !== next.alternativePostalArea)
        return false;
      if (prev.alternativePostalCode !== next.alternativePostalCode)
        return false;
      if (prev.alternativeCountry !== next.alternativeCountry) return false;
    }
  }
  if (prev.useMailForMeeting !== next.useMailForMeeting) return false;
  if (prev.useMailForRelease !== next.useMailForRelease) return false;
  if (prev.useAddressForMeeting !== next.useAddressForMeeting) return false;
  if (prev.useAddressForRelease !== next.useAddressForRelease) return false;
  if (prev.marketingPermission !== next.marketingPermission) return false;
  if (!_isEqual(prev.bankAccounts, next.bankAccounts)) return false;
  if (prev.tax !== next.tax) return false;

  return true;
};
